import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth, roleHomePaths } from './context/AuthContext';
import { FilterProvider } from './context/FilterContext';
import { ThemeProvider } from './context/ThemeContext';

import Topbar from './components/layout/Topbar';
import Sidebar from './components/layout/Sidebar';
import FilterBar from './components/layout/FilterBar';
import Breadcrumb from './components/layout/Breadcrumb';

// Auth pages
import SetupPage from './pages/auth/SetupPage';
import LoginPage from './pages/auth/LoginPage';

// Shared (scope-parameterised)
import OverviewScreen from './pages/shared/OverviewScreen';
import GenerationScreen from './pages/shared/GenerationScreen';

// National
import NationalOverview from './pages/national/NationalOverview';
import NationalGeneration from './pages/national/NationalGeneration';
import NationalStates from './pages/national/NationalStates';
import NationalAlarms from './pages/national/NationalAlarms';
import NationalCompliance from './pages/national/NationalCompliance';

// Consumer
import ConsumerDashboard from './pages/consumer/ConsumerDashboard';
import ConsumerElectrical from './pages/consumer/ConsumerElectrical';
import ConsumerAlarms from './pages/consumer/ConsumerAlarms';
import ConsumerBilling from './pages/consumer/ConsumerBilling';
import ConsumerSupport from './pages/consumer/ConsumerSupport';

// State
import StateOverview from './pages/state/StateOverview';
import StateGeneration from './pages/state/StateGeneration';
import StateAlarms from './pages/state/StateAlarms';
import StateDistricts from './pages/state/StateDistricts';

// DISCOM
import DiscomGridOps from './pages/discom/DiscomGridOps';
import DiscomNetMetering from './pages/discom/DiscomNetMetering';
import DiscomCircles from './pages/discom/DiscomCircles';
import DiscomAlarms from './pages/discom/DiscomAlarms';
import DiscomRemoteControl from './pages/discom/DiscomRemoteControl';

// Vendor
import VendorPlantList from './pages/vendor/VendorPlantList';
import VendorTickets from './pages/vendor/VendorTickets';
import VendorDeviceHealth from './pages/vendor/VendorDeviceHealth';

// Admin
import AdminUsersPage from './pages/admin/AdminUsersPage';

/* ── Loading Spinner ────────────────────────────────────────────────────── */
function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-text-tertiary">Loading…</p>
      </div>
    </div>
  );
}

/* ── Main app layout (authenticated) ───────────────────────────────────── */
function AppLayout() {
  const { isAdmin, adminViewingAs, currentRole } = useAuth();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const homeRoute = roleHomePaths[currentRole];

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Admin "Viewing As" banner */}
      {isAdmin && adminViewingAs && (
        <div className="bg-amber-500 text-white text-xs font-semibold text-center py-1 px-4 flex-shrink-0">
          ⚠ Admin — Viewing as {adminViewingAs} role. Role switcher active.
        </div>
      )}
      <Topbar />
      <div className="flex flex-1 min-h-0">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Breadcrumb />
          {!isAdminRoute && <FilterBar />}
          <main className="flex-1 overflow-y-auto bg-bg-secondary">
            <Routes>
              <Route path="/" element={<Navigate to={homeRoute} replace />} />

              {/* National */}
              <Route path="/national/overview"   element={<NationalOverview />} />
              <Route path="/national/generation" element={<NationalGeneration />} />
              <Route path="/national/states"     element={<NationalStates />} />
              <Route path="/national/alarms"     element={<NationalAlarms />} />
              <Route path="/national/compliance" element={<NationalCompliance />} />

              {/* Consumer */}
              <Route path="/consumer/dashboard"  element={<ConsumerDashboard />} />
              <Route path="/consumer/electrical" element={<ConsumerElectrical />} />
              <Route path="/consumer/alarms"     element={<ConsumerAlarms />} />
              <Route path="/consumer/billing"    element={<ConsumerBilling />} />
              <Route path="/consumer/support"    element={<ConsumerSupport />} />

              {/* State */}
              <Route path="/state/overview"      element={<StateOverview />} />
              <Route path="/state/generation"    element={<StateGeneration />} />
              <Route path="/state/districts"     element={<StateDistricts />} />
              <Route path="/state/alarms"        element={<StateAlarms />} />

              {/* DISCOM */}
              <Route path="/discom/overview"       element={<OverviewScreen scope="discom" />} />
              <Route path="/discom/generation"     element={<GenerationScreen scope="discom" />} />
              <Route path="/discom/grid-ops"       element={<DiscomGridOps />} />
              <Route path="/discom/net-metering"   element={<DiscomNetMetering />} />
              <Route path="/discom/circles"        element={<DiscomCircles />} />
              <Route path="/discom/alarms"         element={<DiscomAlarms />} />
              <Route path="/discom/remote-control" element={<DiscomRemoteControl />} />

              {/* Vendor */}
              <Route path="/vendor/overview"   element={<OverviewScreen scope="vendor" />} />
              <Route path="/vendor/generation" element={<GenerationScreen scope="vendor" />} />
              <Route path="/vendor/portfolio"  element={<Navigate to="/vendor/overview" replace />} />
              <Route path="/vendor/plants"     element={<VendorPlantList />} />
              <Route path="/vendor/tickets"    element={<VendorTickets />} />
              <Route path="/vendor/devices"    element={<VendorDeviceHealth />} />

              {/* Admin-only */}
              <Route
                path="/admin/users"
                element={isAdmin ? <AdminUsersPage /> : <Navigate to={homeRoute} replace />}
              />

              <Route path="*" element={<Navigate to={homeRoute} replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}

/* ── Router with auth gate ──────────────────────────────────────────────── */
function AppRouter() {
  const { isAuthenticated, isLoading, currentRole } = useAuth();
  const navigate = useNavigate();
  const [setupComplete, setSetupComplete]     = useState<boolean | null>(null);
  const [setupChecking, setSetupChecking]     = useState(true);

  useEffect(() => {
    axios.get<{ setupComplete: boolean }>('/api/setup-status')
      .then(res => setSetupComplete(res.data.setupComplete))
      .catch(() => setSetupComplete(true)) // on error, assume setup done → go to login
      .finally(() => setSetupChecking(false));
  }, []);

  // Redirect to setup if first run
  useEffect(() => {
    if (!setupChecking && setupComplete === false) {
      navigate('/setup', { replace: true });
    }
  }, [setupChecking, setupComplete, navigate]);

  if (isLoading || setupChecking) return <FullScreenLoader />;

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/setup" element={setupComplete ? <Navigate to="/login" replace /> : <SetupPage />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to={roleHomePaths[currentRole]} replace /> : <LoginPage />} />

      {/* Protected app routes */}
      <Route
        path="/*"
        element={isAuthenticated ? (
          <FilterProvider>
            <AppLayout />
          </FilterProvider>
        ) : (
          <Navigate to="/login" replace />
        )}
      />
    </Routes>
  );
}

/* ── Root ───────────────────────────────────────────────────────────────── */
export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
